import { useMemo } from 'react';
import { CalendarCheck } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { MetricCard } from '../../components/common/MetricCard';
import { EmptyState } from '../../components/common/EmptyState';
import { BOOKING_STATUSES, PAYMENT_STATUSES, STATUS_FLOW, TH, TD } from './config';
import type { BookingRow, BookingSummary } from './hooks';
import type { Service } from '../../types';

interface TeamUser { id: string; first_name: string; last_name: string; }
interface Props {
  bookings: BookingRow[];
  services: Service[];
  users: TeamUser[];
  summary: BookingSummary;
  onSelect: (b: BookingRow) => void;
  onAdvance: (id: string, status: string) => void;
  onNewBooking?: () => void;
}

function fmt$(n: number): string { return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 0 })}`; }
function fmtTime(t: string): string {
  const [h, m] = t.split(':').map(Number);
  const ap = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ap}`;
}

export function UpcomingTab({ bookings, summary, onSelect, onAdvance, onNewBooking }: Props) {
  const todayStr = new Date().toISOString().split('T')[0];
  const upcoming = useMemo(() =>
    bookings.filter(b => b.date >= todayStr && b.status !== 'cancelled' && b.status !== 'no_show')
      .sort((a, b) => `${a.date}${a.start_time}`.localeCompare(`${b.date}${b.start_time}`)),
  [bookings, todayStr]);

  const todayBookings = upcoming.filter(b => b.date === todayStr);
  const futureBookings = upcoming.filter(b => b.date > todayStr);

  const renderRow = (b: BookingRow) => {
    const st = BOOKING_STATUSES[b.status] ?? BOOKING_STATUSES.pending;
    const ps = PAYMENT_STATUSES[(b.payment_status as string) ?? 'unpaid'] ?? PAYMENT_STATUSES.unpaid;
    const nextIdx = STATUS_FLOW.indexOf(b.status) + 1;
    const next = nextIdx > 0 && nextIdx < STATUS_FLOW.length ? STATUS_FLOW[nextIdx] : null;
    return (
      <tr key={b.id} onClick={() => onSelect(b)} style={{ cursor: 'pointer', transition: 'background 150ms ease' }}
        onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
        <td style={{ ...TD, fontWeight: 500 }}>{fmtTime(b.start_time)} – {fmtTime(b.end_time)}</td>
        <td style={{ ...TD, fontWeight: 600, color: palette.g800 }}>{b.client_name ?? '—'}</td>
        <td style={TD}>{b.service_name ?? '—'}</td>
        <td style={TD}>{b.assigned_name ?? '—'}</td>
        <td style={TD}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: st.bg, color: st.fg }}>{st.label}</span></td>
        <td style={TD}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: ps.bg, color: ps.fg }}>{ps.label}</span></td>
        <td style={TD}>
          {next && (
            <button onClick={e => { e.stopPropagation(); onAdvance(b.id, next); }} style={{
              padding: '4px 10px', borderRadius: 4, border: 'none', fontSize: 11, fontWeight: 600,
              background: palette.accent, color: palette.white, cursor: 'pointer', fontFamily: 'inherit',
            }}>{BOOKING_STATUSES[next]?.label ?? next}</button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <MetricCard label="Today" value={String(summary.todayCount)} />
        <MetricCard label="This week" value={String(summary.weekCount)} />
        <MetricCard label="No-shows (month)" value={String(summary.noShowsMonth)}
          delta={summary.noShowsMonth > 0 ? 'Review' : undefined} deltaType={summary.noShowsMonth > 0 ? 'warn' : undefined} />
        <MetricCard label="Revenue (month)" value={fmt$(summary.revenueMonth)} />
      </div>

      {upcoming.length === 0 ? (
        <EmptyState icon={<CalendarCheck size={22} />} title="No upcoming bookings" subtitle="Create a booking or wait for clients to book." actionLabel="New Booking" onAction={onNewBooking} />
      ) : (
        <div>
          {todayBookings.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: palette.accent, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '8px 0', borderBottom: `1px solid ${palette.g150}` }}>
                Today ({todayBookings.length})
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={TH}>Time</th><th style={TH}>Client</th><th style={TH}>Service</th>
                  <th style={TH}>Assigned</th><th style={TH}>Status</th><th style={TH}>Payment</th><th style={TH}>Action</th>
                </tr></thead>
                <tbody>{todayBookings.map(renderRow)}</tbody>
              </table>
            </div>
          )}
          {futureBookings.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: palette.g400, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '8px 0', borderBottom: `1px solid ${palette.g150}` }}>
                Upcoming ({futureBookings.length})
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                {!todayBookings.length && <thead><tr>
                  <th style={TH}>Date / Time</th><th style={TH}>Client</th><th style={TH}>Service</th>
                  <th style={TH}>Assigned</th><th style={TH}>Status</th><th style={TH}>Payment</th><th style={TH}>Action</th>
                </tr></thead>}
                <tbody>{futureBookings.map(b => (
                  <tr key={b.id} onClick={() => onSelect(b)} style={{ cursor: 'pointer', transition: 'background 150ms ease' }}
                    onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    <td style={{ ...TD, fontWeight: 500 }}>{b.date} {fmtTime(b.start_time)}</td>
                    <td style={{ ...TD, fontWeight: 600, color: palette.g800 }}>{b.client_name ?? '—'}</td>
                    <td style={TD}>{b.service_name ?? '—'}</td>
                    <td style={TD}>{b.assigned_name ?? '—'}</td>
                    <td style={TD}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: (BOOKING_STATUSES[b.status] ?? BOOKING_STATUSES.pending).bg, color: (BOOKING_STATUSES[b.status] ?? BOOKING_STATUSES.pending).fg }}>{(BOOKING_STATUSES[b.status] ?? BOOKING_STATUSES.pending).label}</span></td>
                    <td style={TD}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: (PAYMENT_STATUSES[(b.payment_status as string) ?? 'unpaid'] ?? PAYMENT_STATUSES.unpaid).bg, color: (PAYMENT_STATUSES[(b.payment_status as string) ?? 'unpaid'] ?? PAYMENT_STATUSES.unpaid).fg }}>{(PAYMENT_STATUSES[(b.payment_status as string) ?? 'unpaid'] ?? PAYMENT_STATUSES.unpaid).label}</span></td>
                    <td style={TD}>{STATUS_FLOW.indexOf(b.status) >= 0 && STATUS_FLOW.indexOf(b.status) < STATUS_FLOW.length - 1 && (
                      <button onClick={e => { e.stopPropagation(); onAdvance(b.id, STATUS_FLOW[STATUS_FLOW.indexOf(b.status) + 1]); }} style={{ padding: '4px 10px', borderRadius: 4, border: 'none', fontSize: 11, fontWeight: 600, background: palette.accent, color: palette.white, cursor: 'pointer', fontFamily: 'inherit' }}>{BOOKING_STATUSES[STATUS_FLOW[STATUS_FLOW.indexOf(b.status) + 1]]?.label}</button>
                    )}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
